import dayjs, { Dayjs } from "dayjs";

/**
 * Period-Based class form validation helpers, kept framework-free so they can be
 * unit-tested and shared. Mirrors the backend guards in classesAssignController:
 * a Period-Based class is bounded by [from, to] and only generates sessions on
 * the selected weekdays that fall inside that inclusive range.
 */

/**
 * Does at least one of the selected weekday names land on a date inside the
 * inclusive [from, to] range? Boundary dates count when they match.
 */
export const rangeHasSelectedWeekday = (
  from: Dayjs,
  to: Dayjs,
  days: string[]
): boolean => {
  if (!days.length) return false;
  const wanted = days.map((d) => d.toLowerCase());
  let cursor = from.startOf("day");
  const end = to.startOf("day");
  // Guard against an inverted range producing an unbounded loop.
  if (cursor.isAfter(end)) return false;
  while (!cursor.isAfter(end)) {
    if (wanted.includes(cursor.format("dddd").toLowerCase())) return true;
    cursor = cursor.add(1, "day");
  }
  return false;
};

export interface PeriodFieldErrors {
  period_based_from_date?: string;
  period_based_to_date?: string;
}

/**
 * Field-level errors for the Period-Based date range. Returns an empty object
 * when the type is not Period-Based or everything is valid.
 */
export const validatePeriodRange = (
  isPeriodBased: boolean,
  from: Dayjs | null,
  to: Dayjs | null,
  selectedDays: string[],
  today: Dayjs = dayjs().startOf("day")
): PeriodFieldErrors => {
  const errors: PeriodFieldErrors = {};
  if (isPeriodBased && !from) errors.period_based_from_date = "Start date is required";
  if (isPeriodBased && !to) errors.period_based_to_date = "End date is required";

  if (from && from.startOf("day").isBefore(today)) {
    errors.period_based_from_date = "From date cannot be in the past";
  }

  if (from && to) {
    const f = from.startOf("day");
    const t = to.startOf("day");
    if (t.isBefore(f)) {
      errors.period_based_to_date = "To date must be the same as or after From date";
    } else if (
      isPeriodBased &&
      selectedDays.length > 0 &&
      !rangeHasSelectedWeekday(f, t, selectedDays)
    ) {
      errors.period_based_to_date =
        "None of the selected days fall within this date range";
    }
  }
  return errors;
};

/**
 * Is the Period-Based date range structurally submittable? (Both dates present,
 * end on/after start, and a selected weekday actually inside the range.)
 */
export const isPeriodRangeSubmittable = (
  from: Dayjs | null,
  to: Dayjs | null,
  selectedDays: string[]
): boolean => {
  if (!from || !to) return false;
  if (to.startOf("day").isBefore(from.startOf("day"))) return false;
  return rangeHasSelectedWeekday(from, to, selectedDays);
};
